import { Directive, ElementRef, HostListener, Input, Renderer2 } from '@angular/core';
import { TooltipMethod, TooltipPosition } from '../models/tooltip.model';
@Directive({
  selector: '[zenTooltip]',
  standalone: true,
})
export class TooltipDirective {
  /**
   * Variable which is used to store the timeout ID for showing the tooltip.
   * @type {number}
   */
  showTimeout!: number;
  /**
   * Variable which is used to store the timeout ID for hiding the tooltip.
   * @type {number}
   */
  hideTimeout!: number;
  /**
   * Variable which is used to store the content of the tooltip.
   * @type {string | number}
   */
  @Input() zenTooltip!: string | number;
  /**
   * Variable which is used to store the position of the tooltip relative to the host element.
   * @type {string}
   */
  @Input() tooltipPosition: TooltipPosition = 'below';
  /**
   * Variable which is used to store the delay in milliseconds before showing the tooltip.
   * @type {number}
   */
  @Input() tooltipShowDelay: number = 100;
  /**
   * Variable which is used to store the delay in milliseconds before hiding the tooltip.
   * @type {number}
   */
  @Input() tooltipHideDelay: number = 100;
  /**
   * Variable which is used to indicate whether the tooltip is disabled.
   * @type {boolean}
   */
  @Input() tooltipDisabled: boolean = false;
  /**
   * Variable which is used to specify how the tooltip is activated, such as by 'hover' or 'click'.
   * @type {TooltipMethod}
   */
  @Input() tooltipMethod: TooltipMethod = 'hover';
  /**
   * Variable which is used to store the reference to the tooltip element.
   * @type {HTMLElement | null}
   */
  private tooltipElement: HTMLElement | null = null;
  /**
   * Variable which is used to indicates whether the screen is mobile.
   * @type {boolean}
   */
  isMobileScreenTooltip: boolean = false;
  /**
   * Constructor which is used to inject dependencies and initialize the directive.
   * @param el - Reference to the host element.
   * @param renderer - Renderer2 to manipulate DOM elements.
   */
  constructor(
    private el: ElementRef,
    private renderer: Renderer2) {
  }
  /**
   * Method which is used to handle mouse click events.
   */
  @HostListener('click') onClick() {
    if (!this.isMobileScreenTooltip && this.tooltipMethod === 'click') {
      clearTimeout(this.showTimeout);
      clearTimeout(this.hideTimeout);
      this.showTimeout = window.setTimeout(() => {
        this.createTooltipElement();
      }, this.tooltipShowDelay);
      this.hideTimeout = window.setTimeout(() => {
        this.destroyTooltip();
      }, this.tooltipHideDelay);
    }
  }
  /**
   * Method which is used to handle mouse enter events.
   */
  @HostListener('mouseenter') onMouseEnter() {
    if (!this.isMobileScreenTooltip && this.tooltipMethod === 'hover') {
      clearTimeout(this.hideTimeout);
      this.showTimeout = window.setTimeout(() => {
        this.createTooltipElement();
      }, this.tooltipShowDelay);
    }
  }

  /**
   * Method which is used to handle mouse leave events.
   */
  @HostListener('mouseleave') onMouseLeave() {
    if (!this.isMobileScreenTooltip && this.tooltipMethod === 'hover') {
      clearTimeout(this.showTimeout);
      this.hideTimeout = window.setTimeout(() => {
        this.destroyTooltip();
      }, this.tooltipHideDelay);
    }
  }
  /**
   * Method which is used to handle mouse click events.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.tooltipElement) {
      if (this.isMobileScreenTooltip) {
        this.onTouchEnd();
      }
      else {
        this.onMouseLeave()
      }
    }
  }
  /**
   * Method which is used to handle touch start events.
   */
  @HostListener('touchstart') onTouchStart() {
    if (this.tooltipMethod === 'hover') {
      this.isMobileScreenTooltip = true;
      clearTimeout(this.hideTimeout);
      this.showTimeout = window.setTimeout(() => {
        this.createTooltipElement();
      }, this.tooltipShowDelay);
    }
  }
  /**
 * Method which is used to handle touch end events.
 */
  @HostListener('touchend') onTouchEnd() {
    if (this.tooltipMethod === 'hover') {
      clearTimeout(this.showTimeout);
      this.hideTimeout = window.setTimeout(() => {
        this.destroyTooltip();
      }, this.tooltipHideDelay);
    }
  }
  /**
   * Method which is used to create the tooltip element.
   */
  createTooltipElement() {
    if (!this.tooltipElement && this.zenTooltip && !this.tooltipDisabled) {
      this.tooltipElement = this.renderer.createElement('zen-tooltip');
      if (this.tooltipElement) {
        this.renderer.appendChild(document.body, this.tooltipElement);
        this.tooltipElement.classList.add('zt-element', 'zt-element-show');
        this.updateTooltip();
      }
    }
  }
  /**
   * Method which is used to update the tooltip content and position.
   */
  updateTooltip() {
    this.setToolTipContent();
    this.setTooltipPosition();
  }
  /**
   * Method which is used to set the content of the tooltip.
   */
  setToolTipContent() {
    if (this.zenTooltip && this.tooltipElement) {
      this.tooltipElement.textContent = this.zenTooltip.toString();
    }
  }
  /**
   * Method which is used to set the position of the tooltip.
   */
  setTooltipPosition() {
    if (this.tooltipPosition && this.tooltipElement) {
      const hostPos = this.el.nativeElement.getBoundingClientRect(); // Get host element's position
      const tooltipPos = this.tooltipElement.getBoundingClientRect(); // Get tooltip element's size
      let top: number, left: number;
      // Calculate the position based on the tooltipPosition input
      switch (this.tooltipPosition) {
        case 'above':
          top = hostPos.top - tooltipPos.height - 8;
          left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
          if (top < 0) {
            // If the tooltip goes off the top, move it below
            top = hostPos.bottom + 8;
          }
          break;
        case 'below':
          top = hostPos.bottom + 8;
          left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
          if (top + tooltipPos.height > window.innerHeight) {
            // If the tooltip goes off the bottom, move it above
            top = hostPos.top - tooltipPos.height - 8;
          }
          break;
        case 'before':
          top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
          left = hostPos.left - tooltipPos.width - 8;
          if (left < 0) {
            // If the tooltip goes off the left, move it after
            left = hostPos.right + 8;
          }
          break;
        case 'after':
          top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
          left = hostPos.right + 8;
          if (left + tooltipPos.width > window.innerWidth) {
            // If the tooltip goes off the right, move it before
            left = hostPos.left - tooltipPos.width - 8;
          }
          break;
      }
      // Ensure the tooltip does not go outside the viewport
      top = Math.max(top, 0); // Ensure top is not negative
      left = Math.max(left, 0); // Ensure left is not negative
      this.renderer.setStyle(this.tooltipElement, 'top', `${top + window.scrollY}px`);
      this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);
      this.renderer.setStyle(this.tooltipElement, 'position', 'absolute');
    }
  }
  /**
   * Method which is used to destroy the tooltip element.
   */
  destroyTooltip() {
    if (this.tooltipElement && !this.tooltipDisabled) {
      this.tooltipElement.classList.remove('zt-element-show');
      this.tooltipElement.classList.add('zt-element-hide');
      setTimeout(() => {
        if (this.tooltipElement && this.tooltipElement.parentNode) {
          this.tooltipElement.parentNode.removeChild(this.tooltipElement);
        }
        this.tooltipElement = null;
      }, 1.5);
      this.isMobileScreenTooltip = false;
    }
  }
  /**
   * Method which is used to clean up by destroying the tooltip element.
   */
  ngOnDestroy() {
    clearTimeout(this.showTimeout);
    clearTimeout(this.hideTimeout);
    this.destroyTooltip();
  }
}
